(() => {
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    'object',
    'embed',
    '[contenteditable]',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const getFocusable = (container) => {
    if (!container) return [];
    const nodes = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR));
    return nodes.filter((el) => {
      if (el.hasAttribute('disabled')) return false;
      if (el.getAttribute('aria-disabled') === 'true') return false;
      if (el.hasAttribute('hidden')) return false;
      if (el.closest('[hidden]')) return false;
      return true;
    });
  };

  const initMainNav = (root) => {
    if (!root) return;
    if (root.dataset.mainNavInitialized === 'true') return;

    const overlay = root.querySelector('[data-main-nav-overlay]');
    const drawer = root.querySelector('[data-main-nav-drawer]');
    const toggle = root.querySelector('[data-main-nav-toggle]');
    const toggleLabel = root.querySelector('[data-main-nav-toggle-label]');
    const desktopDropdowns = Array.from(root.querySelectorAll('[data-main-nav-dropdown]'));
    const mobileGroups = Array.from(root.querySelectorAll('[data-main-nav-mobile-group]'));
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

    if (!drawer || !toggle) return;

    root.dataset.mainNavInitialized = 'true';

    let isOpen = root.dataset.mainNavOpen === 'true';
    let restoreOverflow = null;

    const setDropdownState = (dropdown, nextOpen) => {
      const dropdownToggle = dropdown.querySelector('[data-main-nav-dropdown-toggle]');
      const panel = dropdown.querySelector('[data-main-nav-dropdown-panel]');
      if (!dropdownToggle || !panel) return;

      const shouldOpen = Boolean(nextOpen);
      dropdown.dataset.mainNavDropdownOpen = shouldOpen ? 'true' : 'false';
      dropdownToggle.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
      panel.setAttribute('aria-hidden', shouldOpen ? 'false' : 'true');

      if (shouldOpen) panel.removeAttribute('inert');
      else panel.setAttribute('inert', '');
    };

    const closeDropdowns = (except = null) => {
      desktopDropdowns.forEach((dropdown) => {
        if (dropdown !== except) setDropdownState(dropdown, false);
      });
    };

    const openDropdown = (dropdown) => {
      closeDropdowns(dropdown);
      setDropdownState(dropdown, true);
    };

    const setMobileGroupState = (group, nextOpen) => {
      const groupToggle = group.querySelector('[data-main-nav-mobile-toggle]');
      const panel = group.querySelector('[data-main-nav-mobile-panel]');
      if (!groupToggle || !panel) return;

      const shouldOpen = Boolean(nextOpen);
      group.dataset.mainNavMobileOpen = shouldOpen ? 'true' : 'false';
      groupToggle.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
      panel.setAttribute('aria-hidden', shouldOpen ? 'false' : 'true');

      if (shouldOpen) {
        panel.removeAttribute('hidden');
        panel.removeAttribute('inert');
      } else {
        panel.setAttribute('hidden', '');
        panel.setAttribute('inert', '');
      }
    };

    const closeMobileGroups = (except = null) => {
      mobileGroups.forEach((group) => {
        if (group !== except) setMobileGroupState(group, false);
      });
    };

    const setOpenState = (nextOpen) => {
      isOpen = Boolean(nextOpen);
      root.dataset.mainNavOpen = isOpen ? 'true' : 'false';
      closeDropdowns();

      if (!isOpen) closeMobileGroups();

      drawer.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      toggle.setAttribute('aria-label', isOpen ? 'Close menu' : 'Open menu');
      if (toggleLabel) toggleLabel.textContent = isOpen ? 'Close menu' : 'Open menu';

      if (isOpen) {
        drawer.removeAttribute('inert');
      } else {
        drawer.setAttribute('inert', '');
      }
    };

    const lockScroll = () => {
      if (restoreOverflow) return;
      const html = document.documentElement;
      const prev = html.style.overflow;
      html.style.overflow = 'hidden';
      restoreOverflow = () => {
        html.style.overflow = prev;
        restoreOverflow = null;
      };
    };

    const unlockScroll = () => {
      if (!restoreOverflow) return;
      restoreOverflow();
    };

    const setBarHeightVar = () => {
      const bar = root.querySelector('.main-nav__bar');
      if (!bar) return;
      const height = Math.ceil(bar.getBoundingClientRect().height);
      if (height > 0) root.style.setProperty('--main-nav-bar-height', `${height}px`);
    };

    let removeGlobalListeners = null;
    const addGlobalListeners = () => {
      if (removeGlobalListeners) return;

      const onDocumentClick = (event) => {
        if (!isOpen) return;
        const target = event.target;
        if (!(target instanceof Element)) return;

        if (drawer.contains(target)) return;
        if (toggle.contains(target)) return;

        setOpenState(false);
        unlockScroll();
        toggle.focus({ preventScroll: true });
        removeGlobalListeners?.();
      };

      const onDocumentKeydown = (event) => {
        if (!isOpen) return;
        if (event.key !== 'Escape') return;
        event.preventDefault();

        setOpenState(false);
        unlockScroll();
        toggle.focus({ preventScroll: true });
        removeGlobalListeners?.();
      };

      document.addEventListener('click', onDocumentClick, true);
      document.addEventListener('keydown', onDocumentKeydown, true);

      removeGlobalListeners = () => {
        document.removeEventListener('click', onDocumentClick, true);
        document.removeEventListener('keydown', onDocumentKeydown, true);
        removeGlobalListeners = null;
      };
    };

    const open = () => {
      if (isOpen) return;
      setOpenState(true);
      lockScroll();
      addGlobalListeners();
      setBarHeightVar();

      const focusTarget = drawer;
      requestAnimationFrame(() => {
        focusTarget.focus?.({ preventScroll: true });
      });
    };

    const close = () => {
      if (!isOpen) return;
      setOpenState(false);
      unlockScroll();
      removeGlobalListeners?.();
      toggle.focus({ preventScroll: true });
    };

    const toggleOpen = () => {
      if (isOpen) close();
      else open();
    };

    toggle.addEventListener('click', (event) => {
      event.preventDefault();
      toggleOpen();
    });

    desktopDropdowns.forEach((dropdown) => {
      const parentLink = dropdown.querySelector('[data-main-nav-dropdown-parent-link]');
      const dropdownToggle = dropdown.querySelector('[data-main-nav-dropdown-toggle]');
      const panel = dropdown.querySelector('[data-main-nav-dropdown-panel]');
      if (!dropdownToggle || !panel) return;

      let closeTimer = null;
      let openedByHover = false;
      const cancelScheduledClose = () => {
        if (closeTimer === null) return;
        window.clearTimeout(closeTimer);
        closeTimer = null;
      };
      const scheduleClose = () => {
        cancelScheduledClose();
        closeTimer = window.setTimeout(() => {
          closeTimer = null;
          if (!dropdown.contains(document.activeElement)) setDropdownState(dropdown, false);
        }, 120);
      };
      const focusPanelEdge = (last = false) => {
        const links = getFocusable(panel);
        const target = last ? links[links.length - 1] : links[0];
        target?.focus({ preventScroll: true });
      };

      dropdownToggle.addEventListener('click', (event) => {
        event.preventDefault();
        cancelScheduledClose();

        // Pointer entry opens the menu before a mouse click occurs; keep that
        // first click open rather than immediately toggling it closed.
        if (openedByHover) {
          openedByHover = false;
          openDropdown(dropdown);
          return;
        }

        const shouldOpen = dropdown.dataset.mainNavDropdownOpen !== 'true';
        if (shouldOpen) openDropdown(dropdown);
        else setDropdownState(dropdown, false);
      });

      const onParentKeydown = (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          openDropdown(dropdown);
          focusPanelEdge(event.key === 'ArrowUp');
        } else if (event.key === 'Escape') {
          event.preventDefault();
          setDropdownState(dropdown, false);
        }
      };

      parentLink?.addEventListener('keydown', onParentKeydown);
      parentLink?.addEventListener('click', () => setDropdownState(dropdown, false));
      dropdownToggle.addEventListener('keydown', onParentKeydown);

      dropdown.addEventListener('pointerenter', () => {
        if (!finePointer.matches) return;
        cancelScheduledClose();
        if (dropdown.dataset.mainNavDropdownOpen !== 'true') {
          openedByHover = true;
          openDropdown(dropdown);
        }
      });

      dropdown.addEventListener('pointerleave', () => {
        if (!finePointer.matches) return;
        openedByHover = false;
        scheduleClose();
      });

      dropdown.addEventListener('focusin', () => {
        cancelScheduledClose();
      });

      dropdown.addEventListener('focusout', (event) => {
        const nextTarget = event.relatedTarget;
        if (nextTarget instanceof Node && dropdown.contains(nextTarget)) return;
        setDropdownState(dropdown, false);
      });

      panel.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (target.closest('a[href]')) setDropdownState(dropdown, false);
      });

      panel.addEventListener('keydown', (event) => {
        const links = getFocusable(panel);
        if (!links.length) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          setDropdownState(dropdown, false);
          dropdownToggle.focus({ preventScroll: true });
          return;
        }

        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();

        const activeIndex = links.indexOf(document.activeElement);
        let nextIndex = 0;
        if (event.key === 'End') nextIndex = links.length - 1;
        else if (event.key === 'ArrowDown') nextIndex = (activeIndex + 1) % links.length;
        else if (event.key === 'ArrowUp') nextIndex = (activeIndex - 1 + links.length) % links.length;
        links[nextIndex]?.focus({ preventScroll: true });
      });

      setDropdownState(dropdown, false);
    });

    mobileGroups.forEach((group) => {
      const groupToggle = group.querySelector('[data-main-nav-mobile-toggle]');
      if (!groupToggle) return;

      groupToggle.addEventListener('click', (event) => {
        event.preventDefault();
        const shouldOpen = group.dataset.mainNavMobileOpen !== 'true';
        closeMobileGroups(group);
        setMobileGroupState(group, shouldOpen);
      });

      groupToggle.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        setMobileGroupState(group, false);
        groupToggle.focus({ preventScroll: true });
      });

      setMobileGroupState(group, false);
    });

    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (desktopDropdowns.some((dropdown) => dropdown.contains(target))) return;
      closeDropdowns();
    });

    overlay?.addEventListener('click', (event) => {
      event.preventDefault();
      close();
    });

    drawer.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('[data-main-nav-drawer-link]')) return;
      close();
    });

    drawer.addEventListener('keydown', (event) => {
      if (!isOpen) return;
      if (event.key !== 'Tab') return;

      const focusables = getFocusable(drawer);
      if (!focusables.length) {
        event.preventDefault();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || !drawer.contains(active)) {
          event.preventDefault();
          last.focus({ preventScroll: true });
        }
      } else {
        if (active === last) {
          event.preventDefault();
          first.focus({ preventScroll: true });
        }
      }
    });

    // Close navigation that belongs to the previous responsive layout.
    const mq = window.matchMedia('(min-width: 768px)');
    const onMqChange = () => {
      if (mq.matches) close();
      else closeDropdowns();
    };
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onMqChange);
    } else if (typeof mq.addListener === 'function') {
      mq.addListener(onMqChange);
    }

    window.addEventListener('resize', setBarHeightVar, { passive: true });
    setBarHeightVar();

    // Initial state (ensure inert/aria match markup).
    setOpenState(false);
  };

  const initAll = () => {
    const roots = document.querySelectorAll('[data-main-nav]');
    if (!roots.length) return;
    roots.forEach(initMainNav);
  };

  if (window.SolarityMainNav && typeof window.SolarityMainNav.initAll === 'function') {
    window.SolarityMainNav.initAll();
    return;
  }

  window.SolarityMainNav = { initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
